/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { R } from 'retemplate';
import { isInt, compareNumbers, compareBigInts, normalizeString, CIVILDATETIME_HASH_SEED } from './internal/core';
import { getDaysInMonth, getDate, getDatePart, getWeeksInYear, getCalendarDateFromWeekDate, getDaysInYear, getCalendarDateFromOrdinalDate, DatePart } from './internal/date';
import { getTime, getTimePart, TimePart, NANOSECONDS_PER_DAY } from './internal/time';
import { getInstant, getDateOfInstant, getTimeOfInstant } from './internal/instant';
import { dateTimeDiff, dateTimePlus } from './internal/math';
import { DATE, TIME } from './internal/symbols';
import { EPOCH_DAYS, NANOS_OF_DAY } from './internal/types';
import { DATETIME_EXTENDED, DATETIME_BASIC } from './internal/patterns';
import { Temporal, Components, DateDelta, TimeDelta, DateUnit, TimeUnit } from './types';
import { Zone, ZoneOffset } from './Zone';
import { Clock } from './Clock';
import { CivilDate } from './CivilDate';
import { CivilTime } from './CivilTime';
import { OffsetDateTime } from './OffsetDateTime';
import { Instant } from './Instant';
import { Duration } from './Duration';
import { Equatable, Comparable, Equaler } from '@esfx/equatable';

const dateTimeExtendedRegExp = new RegExp(R`^${DATETIME_EXTENDED}$`);
const dateTimeBasicRegExp = new RegExp(R`^${DATETIME_BASIC}$`);

export class CivilDateTime implements Temporal<CivilDateTime>, Equatable, Comparable {
    /* @internal */ private [DATE]: EPOCH_DAYS;
    /* @internal */ private [TIME]: NANOS_OF_DAY;

    constructor(year: number, month: number, day: number, hour: number, minute: number, second: number = 0, millisecond: number = 0, nanosecond: number = 0) {
        if (!isInt(year)) throw new RangeError(`The value ${year} is not a valid year.`);
        if (!isInt(month) || month < 1 || month > 12) throw new RangeError(`The value ${month} is not a valid month.`);
        if (!isInt(day) || day < 1 || day > getDaysInMonth(year, month)) throw new RangeError(`The value ${day} is not a valid day.`);
        if (!isInt(hour) || hour < 0 || hour > 24) throw new RangeError(`The value ${hour} is not a valid hour.`);
        if (!isInt(minute) || minute < 0 || minute > 59) throw new RangeError(`The value ${minute} is not a valid minute.`);
        if (!isInt(second) || second < 0 || second > 59) throw new RangeError(`The value ${second} is not a valid second.`);
        if (!isInt(millisecond) || millisecond < 0 || millisecond > 1e3) throw new RangeError(`The value ${millisecond} is not a valid millisecond.`);
        if (!isInt(nanosecond) || nanosecond < 0 || nanosecond > 1e6) throw new RangeError(`The value ${nanosecond} is not a valid nanosecond.`);
        if (hour === 24 && minute !== 0) throw new RangeError(`The value ${minute} is not a valid minute of hour ${hour}.`);
        if (hour === 24 && second !== 0) throw new RangeError(`The value ${second} is not a valid second of hour ${hour}.`);
        if (hour === 24 && millisecond !== 0) throw new RangeError(`The value ${millisecond} is not a valid second of hour ${millisecond}.`);
        if (hour === 24 && nanosecond !== 0) throw new RangeError(`The value ${nanosecond} is not a valid second of hour ${nanosecond}.`);
        this[DATE] = getDate(year, month, day);
        this[TIME] = getTime(hour, minute, second, millisecond, nanosecond);
    }

    get year() { return getDatePart(this[DATE], DatePart.year); }
    get month() { return getDatePart(this[DATE], DatePart.month); }
    get day() { return getDatePart(this[DATE], DatePart.day); }
    get dayOfYear() { return getDatePart(this[DATE], DatePart.dayOfYear); }
    get dayOfWeek() { return getDatePart(this[DATE], DatePart.dayOfWeek); }
    get weekOfYear() { return getDatePart(this[DATE], DatePart.weekOfYear); }
    get hour() { return this[TIME] === NANOSECONDS_PER_DAY ? 24 : getTimePart(this[TIME], TimePart.hour); }
    get minute() { return getTimePart(this[TIME], TimePart.minute); }
    get second() { return getTimePart(this[TIME], TimePart.second); }
    get millisecond() { return getTimePart(this[TIME], TimePart.millisecond); }
    get nanosecond() { return getTimePart(this[TIME], TimePart.nanosecond); }

    plus(deltas: Components<DateDelta | TimeDelta> = {}) {
        const { years = 0, months = 0, days = 0, hours = 0, minutes = 0, seconds = 0, milliseconds = 0, nanoseconds = 0 } = deltas;
        if (!isInt(years)) throw new RangeError(`The value ${years} is not valid for 'years' as it is not an integer.`);
        if (!isInt(months)) throw new RangeError(`The value ${months} is not valid for 'months' as it is not an integer.`);
        if (!isInt(days)) throw new RangeError(`The value ${days} is not valid for 'days' as it is not an integer.`);
        if (!isInt(hours)) throw new RangeError(`The value ${hours} is not valid for 'hours' as it is not an integer.`);
        if (!isInt(minutes)) throw new RangeError(`The value ${minutes} is not valid for 'minutes' as it is not an integer.`);
        if (!isInt(seconds)) throw new RangeError(`The value ${seconds} is not valid for 'seconds' as it is not an integer.`);
        if (!isInt(milliseconds)) throw new RangeError(`The value ${milliseconds} is not valid for 'milliseconds' as it is not an integer.`);
        if (!isInt(nanoseconds)) throw new RangeError(`The value ${nanoseconds} is not valid for 'nanoseconds' as it is not an integer.`);
        const instant = dateTimePlus(this[DATE], this[TIME], years, months, days, hours, minutes, seconds, milliseconds, nanoseconds);
        return CivilDateTime.fromDayOfEpoch(getDateOfInstant(instant), getTimeOfInstant(instant));
    }

    minus(deltas: Components<DateDelta | TimeDelta> = {}) {
        const { years = 0, months = 0, days = 0, hours = 0, minutes = 0, seconds = 0, milliseconds = 0, nanoseconds = 0 } = deltas;
        if (!isInt(years)) throw new RangeError(`The value ${years} is not valid for 'years' as it is not an integer.`);
        if (!isInt(months)) throw new RangeError(`The value ${months} is not valid for 'months' as it is not an integer.`);
        if (!isInt(days)) throw new RangeError(`The value ${days} is not valid for 'days' as it is not an integer.`);
        if (!isInt(hours)) throw new RangeError(`The value ${hours} is not valid for 'hours' as it is not an integer.`);
        if (!isInt(minutes)) throw new RangeError(`The value ${minutes} is not valid for 'minutes' as it is not an integer.`);
        if (!isInt(seconds)) throw new RangeError(`The value ${seconds} is not valid for 'seconds' as it is not an integer.`);
        if (!isInt(milliseconds)) throw new RangeError(`The value ${milliseconds} is not valid for 'milliseconds' as it is not an integer.`);
        if (!isInt(nanoseconds)) throw new RangeError(`The value ${nanoseconds} is not valid for 'nanoseconds' as it is not an integer.`);
        const instant = dateTimePlus(this[DATE], this[TIME], -years, -months, -days, -hours, -minutes, -seconds, -milliseconds, -nanoseconds);
        return CivilDateTime.fromDayOfEpoch(getDateOfInstant(instant), getTimeOfInstant(instant));
    }

    with(components: Components<DateUnit | TimeUnit> = {}) {
        const {
            year = this.year,
            month = this.month,
            day = this.day,
            hour = this.hour,
            minute = this.minute,
            second = this.second,
            millisecond = this.millisecond,
            nanosecond = this.nanosecond
        } = components;
        return new CivilDateTime(year, month, day, hour, minute, second, millisecond, nanosecond);
    }

    withOffset(offset: ZoneOffset) {
        return new OffsetDateTime(
            offset,
            this.year,
            this.month,
            this.day,
            this.hour,
            this.minute,
            this.second,
            this.millisecond,
            this.nanosecond);
    }

    withZone(zone: Zone | string) {
        return this
            .toInstant()
            .withZone(Zone.UTC)
            .withZoneSameLocal(zone);
    }

    until(other: CivilDateTime) {
        if (!(other instanceof CivilDateTime)) throw new TypeError("CivilDateTime expected: other");
        return new Duration(dateTimeDiff(this[DATE], this[TIME], other[DATE], other[TIME]));
    }

    since(other: CivilDateTime) {
        if (!(other instanceof CivilDateTime)) throw new TypeError("CivilDateTime expected: other");
        return other.until(this);
    }

    toCivilDate() {
        return CivilDate.fromDayOfEpoch(this[DATE]);
    }

    toCivilTime() {
        return CivilTime.fromNanoseconds(this[TIME]);
    }

    toInstant() {
        return new Instant(getInstant(this[DATE], this[TIME]));
    }

    toCalendarDateTimeString() {
        return `${this.toCivilDate().toCalendarDateString()}T${this.toCivilTime().toString()}`;
    }

    toWeekDateTimeString() {
        return `${this.toCivilDate().toWeekDateString()}T${this.toCivilTime().toString()}`;
    }

    toOrdinalDateTimeString() {
        return `${this.toCivilDate().toOrdinalDateString()}T${this.toCivilTime().toString()}`;
    }

    toString() {
        return this.toCalendarDateTimeString();
    }

    toISOString() {
        return this.toCalendarDateTimeString();
    }

    toJSON() {
        return this.toCalendarDateTimeString();
    }

    equals(other: CivilDateTime) {
        return other !== null
            && other !== undefined
            && this[DATE] === other[DATE]
            && this[TIME] === other[TIME];
    }

    compareTo(other: CivilDateTime): number {
        return compareNumbers(this[DATE], other[DATE])
            || compareBigInts(this[TIME], other[TIME]);
    }

    static from(date: CivilDate, time: CivilTime): CivilDateTime {
        return CivilDateTime.fromDayOfEpoch(date[DATE], time[TIME]);
    }

    static fromDayOfEpoch(days: number, nanoseconds = 0n): CivilDateTime {
        if (!isInt(days)) throw new RangeError(`The value ${days} is not valid for the number of days.`);
        if (nanoseconds < 0 || nanoseconds > NANOSECONDS_PER_DAY) throw new RangeError(`The value ${nanoseconds} is not valid for the number of nanoseconds since the start of the day.`);
        return Object.create(CivilDateTime.prototype, {
            [DATE]: { value: days },
            [TIME]: { value: nanoseconds }
        });
    }

    static fromCalendarDateTime(year: number, month: number, day: number, hour: number, minute: number, second: number = 0, millisecond: number = 0, nanosecond: number = 0) {
        return new CivilDateTime(year, month, day, hour, minute, second, millisecond, nanosecond);
    }

    static fromWeekDateTime(year: number, weekOfYear: number, dayOfWeek: number, hour: number, minute: number, second: number = 0, millisecond: number = 0, nanosecond: number = 0) {
        if (!isInt(year)) throw new RangeError(`The value ${year} is not a valid year.`);
        if (!isInt(weekOfYear) || weekOfYear < 1 || weekOfYear > getWeeksInYear(year)) throw new RangeError(`The value ${weekOfYear} is not a valid week for the year ${year}.`);
        if (!isInt(dayOfWeek) || dayOfWeek < 1 || dayOfWeek > 7) throw new RangeError(`The value ${dayOfWeek} is not a valid day of the week.`);
        const components = getCalendarDateFromWeekDate(year, weekOfYear, dayOfWeek);
        return CivilDateTime.fromCalendarDateTime(
            components.year,
            components.month,
            components.day,
            hour,
            minute,
            second,
            millisecond,
            nanosecond);
    }

    static fromOrdinalDateTime(year: number, dayOfYear: number, hour: number, minute: number, second: number = 0, millisecond: number = 0, nanosecond: number = 0) {
        if (!isInt(year)) throw new RangeError(`The value ${year} is not a valid year.`);
        if (!isInt(dayOfYear) || dayOfYear < 1 || dayOfYear > getDaysInYear(year)) throw new RangeError(`The value ${dayOfYear} is not a valid day for the year ${year}.`);
        const components = getCalendarDateFromOrdinalDate(year, dayOfYear);
        return CivilDateTime.fromCalendarDateTime(
            components.year,
            components.month,
            components.day,
            hour,
            minute,
            second,
            millisecond,
            nanosecond);
    }

    static fromString(text: string) {
        const normalText = normalizeString(text);
        const match = dateTimeExtendedRegExp.exec(normalText) || dateTimeBasicRegExp.exec(normalText);
        if (match) {
            const { year, month, day, weekOfYear, dayOfWeek, dayOfYear, hour, minute, second, millisecond = "", nanosecond = "" } = match.groups!;
            if (year && month && day) return CivilDateTime.fromCalendarDateTime(+year, +month, +day, +hour, +minute, +second, +millisecond.padEnd(3, "0"), +nanosecond.padEnd(6, "0"));
            if (year && weekOfYear && dayOfWeek) return CivilDateTime.fromWeekDateTime(+year, +weekOfYear, +dayOfWeek, +hour, +minute, +second, +millisecond.padEnd(3, "0"), +nanosecond.padEnd(6, "0"));
            if (year && dayOfYear) return CivilDateTime.fromOrdinalDateTime(+year, +dayOfYear, +hour, +minute, +second, +millisecond.padEnd(3, "0"), +nanosecond.padEnd(6, "0"));
        }

        throw new SyntaxError(`Invalid date-time string '${text}'`);
    }

    static now(clock: Clock) {
        return clock.currentInstant().withZone(clock.zone).toCivilDateTime();
    }

    [Equatable.equals](other: unknown) {
        return other instanceof CivilDateTime
            && this.equals(other);
    }

    [Equatable.hash]() {
        let hc = CIVILDATETIME_HASH_SEED;
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this[DATE]);
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this[TIME]);
        return hc;
    }

    [Comparable.compareTo](other: unknown) {
        if (!(other instanceof CivilDateTime)) throw new TypeError();
        return this.compareTo(other);
    }
}

Object.defineProperty(CivilDateTime.prototype, Symbol.toStringTag, {
    writable: true,
    configurable: true,
    value: "CivilDateTime"
});